import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert } from './ui/alert';
import { UserPlus, Loader2 } from 'lucide-react';
import { userManagementService } from '../services/user-management-service';
import { useToast } from '../hooks/use-toast';

interface SignupTokenScreenProps {
  token: string;
  isInviteLink?: boolean;
  onSuccess: (user: any) => void;
}

export function SignupTokenScreen({ token, isInviteLink = false, onSuccess }: SignupTokenScreenProps) {
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [tenantInfo, setTenantInfo] = useState<{ name: string; id: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const toast = useToast();

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setIsValidating(true);
      console.log('🔍 Validando token:', token, 'isInviteLink:', isInviteLink);
      
      if (isInviteLink) {
        // Validar token permanente de invite
        const result = await userManagementService.validateTenantInviteToken(token);
        
        console.log('📋 Resultado da validação de invite:', result);
        
        if (!result.success || !result.tenant) {
          console.error('❌ Token de invite inválido:', result.error);
          setError(result.error || 'Link de convite inválido');
          setIsValid(false);
        } else {
          console.log('✅ Token de invite válido!', result.tenant);
          setTenantInfo(result.tenant);
          setIsValid(true);
        }
      } else {
        // Validar token temporário antigo
        const result = await userManagementService.validateSignupToken(token);
        
        console.log('📋 Resultado da validação:', result);
        
        if (!result.success) {
          console.error('❌ Token inválido:', result.error);
          setError(result.error || 'Token inválido');
          setIsValid(false);
        } else {
          console.log('✅ Token válido!');
          setIsValid(true);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao validar token:', error);
      setError('Erro ao validar token');
      setIsValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      let result;
      
      if (isInviteLink && tenantInfo) {
        // Criar conta usando link permanente de invite
        result = await userManagementService.createAccountWithInviteToken(
          token,
          tenantInfo.id,
          formData.name,
          formData.email,
          formData.password
        );
      } else {
        // Criar conta usando token temporário antigo
        result = await userManagementService.createAccountWithSignupToken(
          token,
          formData.name,
          formData.email,
          formData.password
        );
      }

      if (!result.success) {
        setError(result.error || 'Erro ao criar conta');
        return;
      }

      toast.success('Conta criada com sucesso!');
      onSuccess(result.user);
    } catch (error: any) {
      setError(error.message || 'Erro ao criar conta');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Validando convite...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <img 
              src="/space-logo-black.png" 
              alt="Space AI" 
              className="h-12 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Convite Inválido
            </h1>
          </div>

          <Alert variant="destructive" className="mb-6">
            {error || 'Este link de convite é inválido ou expirou'}
          </Alert>

          <div className="text-center text-sm text-gray-600">
            <p>Por favor, solicite um novo convite ao administrador.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <img 
            src="/space-logo-black.png" 
            alt="Space AI" 
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isInviteLink && tenantInfo ? `Junte-se a ${tenantInfo.name}` : 'Complete seu Cadastro'}
          </h1>
          <p className="text-gray-600 text-sm">
            {isInviteLink && tenantInfo 
              ? `Você foi convidado para se juntar a ${tenantInfo.name}` 
              : 'Você foi convidado para fazer parte da equipe'
            }
          </p>
        </div>

        {/* Mensagem de Sucesso */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">
                Convite válido
              </p>
              <p className="text-xs text-green-700 mt-1">
                Preencha os dados abaixo para criar sua conta
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome Completo</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Digite seu nome completo"
              required
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="seu@email.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Mínimo 6 caracteres"
              required
            />
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirmar Senha</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Confirme sua senha"
              required
            />
          </div>

          {error && (
            <Alert variant="destructive">
              {error}
            </Alert>
          )}

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Criando conta...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                Criar Conta
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Ao criar sua conta, você concorda com nossos Termos de Uso e Política de Privacidade
          </p>
        </div>
      </div>
    </div>
  );
}

